import * as React from "react"

import { Skeleton } from "@ibada/ui"

import { useTranslations } from "@/i18n"
import { useWidgetMenu } from "@ibada/dashboard-engine"

import { WidgetBoundary } from "@/features/finance/overview/components/widget-boundary"
import { ProgressBar } from "@/features/finance/overview/components/progress-bar"
import { formatMoneySafe, formatPercent } from "@/features/finance/overview/lib/overview-format"
import { shareOfTotal, useGlobalBranchRows } from "@/features/finance/overview/lib/use-global-branch-rows"

interface ContributionRow {
  id: string
  name: string
  income: string
  share: number
}

/**
 * Where the church's income actually comes from — each branch's share of total giving.
 *
 * GLOBAL only, and it is the question a table of absolute figures answers badly: "Mon Église $1,500 / Parish
 * $67.90" makes the reader do the division. The share is what tells a leader whether the church rests on one
 * branch, and how exposed it is if that branch has a bad quarter.
 *
 * The percentages are presentation over server-provided figures, not a re-derived total (ADR-0011): the branch
 * rows and the workspace income total both arrive from the consolidated endpoint.
 */
export function BranchContributionWidget() {
  const t = useTranslations("finance.overview")
  const { rows, totalIncome, currency, isLoading, isError, isFetching, refetch } = useGlobalBranchRows()

  const data = React.useMemo(() => {
    const contributions: ContributionRow[] = rows
      .map((row) => ({
        id: row.id,
        name: row.name,
        income: row.income,
        // Share of the SERVER's church-wide income, not of a browser-side sum of these rows.
        share: shareOfTotal(row.income, totalIncome),
      }))
      // A branch that brought nothing in adds a 0% line without adding information — Silent branches covers it.
      .filter((row) => row.share > 0)
      .sort((a, b) => b.share - a.share)

    return { rows: contributions, currency, leadShare: contributions[0]?.share ?? 0 }
  }, [rows, totalIncome, currency])

  useWidgetMenu({ refresh: refetch })

  return (
    <WidgetBoundary
      isLoading={isLoading}
      isError={isError}
      isFetching={isFetching}
      data={data}
      isEmpty={(d) => d.rows.length === 0}
      skeleton={<ContributionSkeleton />}
      errorTitle={t("widgets.branchContribution.error")}
      onRetry={refetch}
      retryLabel={t("widgets.common.retry")}
      emptyTitle={t("widgets.branchContribution.empty.title")}
    >
      {(d) => (
        <div className='space-y-3'>
          <p className='text-muted-foreground text-xs'>
            {t("widgets.branchContribution.lead", { percent: formatPercent(d.leadShare) })}
          </p>
          <ul className='space-y-2.5'>
            {d.rows.map((row) => (
              <li key={row.id} className='space-y-1'>
                <div className='flex items-baseline justify-between gap-3 text-sm'>
                  <span className='min-w-0 flex-1 truncate'>{row.name}</span>
                  <span className='shrink-0 font-medium tabular-nums'>{formatMoneySafe(row.income, d.currency)}</span>
                  <span className='text-muted-foreground w-10 shrink-0 text-right text-xs tabular-nums'>
                    {formatPercent(row.share)}
                  </span>
                </div>
                <ProgressBar value={row.share} tone='success' aria-label={row.name} />
              </li>
            ))}
          </ul>
        </div>
      )}
    </WidgetBoundary>
  )
}

function ContributionSkeleton() {
  return (
    <div className='space-y-2.5'>
      {[70, 55, 40].map((w, index) => (
        <div key={index} className='space-y-1'>
          <div className='flex items-center justify-between'>
            <Skeleton className='h-3.5' style={{ width: `${w}%` }} />
            <Skeleton className='h-3.5 w-14' />
          </div>
          <Skeleton className='h-1.5 w-full' />
        </div>
      ))}
    </div>
  )
}
